import { useEffect, useMemo, useState } from "react";
import { Sliders, Check, X, RotateCcw, Search, Cog } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";

/**
 * Platform Admin → Tenant Features
 *
 * Matrix view of every tenant × every feature. Each cell has 3 states:
 *   1. Override ON        → solid green pill  "On"
 *   2. Override OFF       → solid red pill    "Off"
 *   3. Inherit default    → dashed pill       "On"/"Off" (grey)
 *
 * Clicking cycles: Inherit → Override On → Override Off → Inherit.
 *
 * Platform defaults for each feature sit above the matrix and can be
 * flipped independently — they change the "inherit" behaviour for every
 * tenant that hasn't overridden.
 */
export default function TenantFeaturesAdmin() {
  const [features, setFeatures] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [tenants, setTenants] = useState([]);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [defaultsSaving, setDefaultsSaving] = useState(false);
  // Per-tenant edit modal — deep dive on one tenant's 10 toggles. Null
  // when closed; the tenant object when open.
  const [editingTenant, setEditingTenant] = useState(null);

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get("/admin/tenant-features");
      setFeatures(data.features || []);
      setDefaults(data.defaults || {});
      setTenants(data.tenants || []);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed to load"); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return tenants;
    const s = q.trim().toLowerCase();
    return tenants.filter((t) => (t.name || "").toLowerCase().includes(s));
  }, [tenants, q]);

  // Cycle: inherit → override-on → override-off → inherit
  const nextState = (isOverride, overrideVal) => {
    if (!isOverride) return true;            // → override ON
    if (overrideVal === true) return false;  // → override OFF
    return null;                              // → inherit
  };

  const toggleCell = async (tenant, featureKey) => {
    if (busy) return;
    const override = tenant.overrides?.[featureKey];
    const isOverride = override !== undefined;
    const target = nextState(isOverride, override);
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/tenant-features/${tenant.id}/cell`, {
        feature_key: featureKey,
        enabled: target,
      });
      setTenants((all) => all.map((t) => t.id === tenant.id
        ? { ...t, overrides: data.overrides, effective: data.effective }
        : t));
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to save");
    } finally { setBusy(false); }
  };

  const resetCell = async (tenant, featureKey) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/tenant-features/${tenant.id}/cell`, {
        feature_key: featureKey,
        enabled: null,
      });
      setTenants((all) => all.map((t) => t.id === tenant.id
        ? { ...t, overrides: data.overrides, effective: data.effective }
        : t));
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to save");
    } finally { setBusy(false); }
  };

  const toggleDefault = async (featureKey) => {
    setDefaultsSaving(true);
    const next = { ...defaults, [featureKey]: !defaults[featureKey] };
    try {
      const { data } = await api.put("/admin/tenant-features/defaults",
        { defaults: next });
      setDefaults(data.defaults);
      // Effective values change for every tenant WITHOUT an override for
      // this feature — safest is a full reload.
      load();
      toast.success("Platform default updated");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to save");
    } finally { setDefaultsSaving(false); }
  };

  const renderCell = (tenant, featureKey) => {
    const override = tenant.overrides?.[featureKey];
    const effective = !!tenant.effective?.[featureKey];
    const isOverride = override !== undefined;
    const cls = isOverride
      ? (effective ? "ok" : "err")
      : "";
    const style = isOverride
      ? { cursor: "pointer" }
      : { cursor: "pointer", borderStyle: "dashed", opacity: 0.65 };
    return (
      <div className="flex items-center justify-center gap-1"
        data-testid={`tf-cell-${tenant.id}-${featureKey}`}>
        <button className={`vb-pill ${cls}`} style={style}
          title={isOverride
            ? `Override: ${effective ? "On" : "Off"} — click to cycle`
            : `Inheriting platform default (${effective ? "On" : "Off"}) — click to override`}
          onClick={() => toggleCell(tenant, featureKey)}
          data-testid={`tf-cell-btn-${tenant.id}-${featureKey}`}>
          {effective ? <><Check size={12} /> On</> : <><X size={12} /> Off</>}
        </button>
        {isOverride && (
          <button className="vb-btn ghost" style={{ padding: 3 }}
            title="Revert to platform default"
            onClick={() => resetCell(tenant, featureKey)}
            data-testid={`tf-cell-reset-${tenant.id}-${featureKey}`}>
            <RotateCcw size={12} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="vb-fade-in" data-testid="tenant-features-page">
      <div className="flex items-center gap-3 mb-2">
        <Sliders size={26} style={{ color: "var(--vb-accent)" }} />
        <h1 className="vb-heading text-3xl" style={{ color: "var(--vb-text-2)" }}>Tenant Features</h1>
      </div>
      <p className="opacity-70 mb-6 max-w-3xl">
        Enable or disable individual features per tenant. <b>Solid</b> cells are
        per-tenant overrides; <b>dashed</b> cells inherit the platform default.
        Click a cell to cycle: <em>Inherit → Override On → Override Off → Inherit</em>.
        Report assessments and dashboards will skip disabled features rather
        than count them as missing.
      </p>

      {err && <div className="vb-pill err mb-4" data-testid="tf-error">{err}</div>}

      {/* Platform defaults strip */}
      <div className="vb-card p-3 mb-4" data-testid="tf-defaults-strip">
        <div className="text-xs uppercase font-semibold opacity-70 mb-2">Platform defaults</div>
        <div className="flex flex-wrap gap-2">
          {features.map((f) => (
            <button key={f.key} className={`vb-pill ${defaults[f.key] ? "ok" : "err"}`}
              style={{ cursor: "pointer", opacity: defaultsSaving ? 0.5 : 1 }}
              disabled={defaultsSaving}
              onClick={() => toggleDefault(f.key)}
              data-testid={`tf-default-${f.key}`}>
              {f.label} · {defaults[f.key] ? "On" : "Off"}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 mb-3" style={{ maxWidth: 360 }}>
        <div className="vb-input flex items-center gap-2" style={{ padding: "6px 10px" }}>
          <Search size={14} />
          <input className="border-0 outline-0 bg-transparent flex-1" placeholder="Search tenants"
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="tf-search" />
        </div>
      </div>

      {/* Matrix */}
      <div className="vb-card overflow-auto" style={{ maxWidth: "100%" }}>
        <table className="vb-table" style={{ minWidth: 900 }}>
          <thead>
            <tr>
              <th style={{ position: "sticky", left: 0, zIndex: 2, background: "var(--vb-surface, #fff)" }}>Tenant</th>
              {features.map((f) => (
                <th key={f.key} style={{ textAlign: "center", minWidth: 110 }}>{f.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={features.length + 1} className="opacity-60 text-center py-4">
                No tenants match "{q}".
              </td></tr>
            )}
            {filtered.map((t) => (
              <tr key={t.id} data-testid={`tf-row-${t.id}`}>
                <td style={{ position: "sticky", left: 0, background: "var(--vb-surface, #fff)" }}>
                  <div className="flex items-center gap-2">
                    <div>
                      <div><strong>{t.name}</strong></div>
                      <div className="text-xs opacity-60">
                        {t.site_count} site{t.site_count === 1 ? "" : "s"} · {t.member_count} member{t.member_count === 1 ? "" : "s"}
                      </div>
                    </div>
                    <button className="vb-btn ghost" style={{ padding: 4 }}
                      title="Configure all 10 features for this tenant"
                      onClick={() => setEditingTenant(t)}
                      data-testid={`tf-edit-row-${t.id}`}>
                      <Cog size={14} />
                    </button>
                  </div>
                </td>
                {features.map((f) => (
                  <td key={f.key} style={{ textAlign: "center" }}>
                    {renderCell(t, f.key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="opacity-60 text-xs mt-3">
        Overrides survive a default change — that is what an override is.
        A feature disabled via <a href="/app/feature-rollout">Feature Rollout</a> stays
        off for every tenant regardless of any override here.
      </p>

      {/* Per-tenant deep-dive modal — 10 toggles for one tenant. Cell
          clicks in the matrix are fastest for one cell; this modal is
          faster when reconfiguring many features at once. */}
      {editingTenant && (
        <EditTenantModal
          tenant={editingTenant} features={features} defaults={defaults}
          onClose={() => setEditingTenant(null)}
          onSaved={(freshRow) => {
            setTenants((all) => all.map((x) => x.id === editingTenant.id ? { ...x, ...freshRow } : x));
            setEditingTenant(null);
          }}
        />
      )}
    </div>
  );
}

/** Focused edit modal — 10 toggles with tri-state (On / Off / Inherit). */
function EditTenantModal({ tenant, features, defaults, onClose, onSaved }) {
  // Start from current overrides so unset keys stay "inherit".
  const [draft, setDraft] = useState({ ...(tenant.overrides || {}) });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const cycle = (key) => {
    setDraft((d) => {
      const cur = d[key];
      const next = { ...d };
      if (cur === undefined) next[key] = true;
      else if (cur === true) next[key] = false;
      else delete next[key];
      return next;
    });
  };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const { data } = await api.put(`/admin/tenant-features/${tenant.id}/bulk`,
        { overrides: draft });
      toast.success("Feature overrides saved");
      onSaved({ overrides: data.overrides, effective: data.effective });
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="vb-modal-bg" onClick={onClose}>
      <div className="vb-modal" onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 640, maxHeight: "90vh", overflowY: "auto" }}
        data-testid="tf-edit-modal">
        <div className="flex items-center gap-2 mb-3">
          <Cog size={20} style={{ color: "var(--vb-accent)" }} />
          <h2 className="m-0">Configure features · {tenant.name}</h2>
        </div>
        <p className="text-xs opacity-70 mb-3">
          Tap a row to cycle <em>Inherit → Override On → Override Off → Inherit</em>.
          Inherit uses the platform default (shown in the right column).
        </p>
        {err && <div className="vb-pill err mb-3">{err}</div>}
        <table className="vb-table w-full mb-4">
          <thead>
            <tr>
              <th>Feature</th>
              <th style={{ textAlign: "center", width: 130 }}>State</th>
              <th style={{ textAlign: "center", width: 110 }}>Platform default</th>
            </tr>
          </thead>
          <tbody>
            {features.map((f) => {
              const override = draft[f.key];
              const isOverride = override !== undefined;
              const effective = isOverride ? override : !!defaults[f.key];
              const cls = isOverride ? (effective ? "ok" : "err") : "";
              const style = isOverride ? {} : { borderStyle: "dashed", opacity: 0.65 };
              return (
                <tr key={f.key} data-testid={`tf-edit-row-toggle-${f.key}`}>
                  <td>{f.label}</td>
                  <td style={{ textAlign: "center" }}>
                    <button className={`vb-pill ${cls}`}
                      style={{ ...style, cursor: "pointer" }}
                      onClick={() => cycle(f.key)}
                      data-testid={`tf-edit-cell-${f.key}`}
                      title={isOverride
                        ? `Override: ${effective ? "On" : "Off"}`
                        : `Inheriting default (${defaults[f.key] ? "On" : "Off"})`}>
                      {effective ? <><Check size={12} /> On</> : <><X size={12} /> Off</>}
                    </button>
                  </td>
                  <td className="text-xs opacity-70" style={{ textAlign: "center" }}>
                    {defaults[f.key] ? "On" : "Off"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="flex gap-2 justify-end">
          <button className="vb-btn ghost" onClick={onClose}
            data-testid="tf-edit-cancel">Cancel</button>
          <button className="vb-btn" onClick={save} disabled={saving}
            data-testid="tf-edit-save">
            {saving ? "Saving…" : "Save all"}
          </button>
        </div>
      </div>
    </div>
  );
}
